import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Mail, Phone, Calendar, AlertTriangle, Ban, Play, Search, Download, Clock, CheckCircle, AlertCircle, XCircle, ChevronLeft, ChevronRight, BookOpen, DollarSign, RefreshCw, ArrowRight, TrendingUp, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useToast } from "@/hooks/use-toast";
import { addDays } from "date-fns";
import { db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  updateDoc,
  addDoc,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  getDoc,
  increment,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

interface BorrowingRecord {
  id: string;
  bookId: string;
  bookTitle: string;
  memberId: string;
  lookupId: string;
  memberName: string;
  borrowDate: string;
  dueDate: string;
  returnDate?: string;
  status: "borrowed" | "returned";
  fineAmount: number;
  renewCount: number;
  maxRenewals: number;
  category?: string;
}

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  shelf: string;
  quantity: number;
  available: number;
  status: "available" | "borrowed" | "reserved";
  coverImage?: string;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface UnifiedMember {
  id: string;
  name: string;
  email: string;
  memberId: string;
  role: "student" | "staff";
  phone?: string;
  joinDate: string;
  status: "active" | "suspended" | "expired";
  borrowedBooks: number;
  maxBorrowLimit: number;
  class?: string;
  grade?: string;
  department?: string;
  middleName?: string;
  dateOfBirth?: string;
  gender?: string;
  admissionGrade?: string;
  currentClassUid?: string;
  dormitory?: string;
  guardians?: Array<{ name: string; phone: string; email: string; address: string }>;
  medicalInfo?: string;
  position?: string;
  tscNumber?: string;
  kraPin?: string;
  subjectsOfExpertiseId?: string[];
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CurrentBook {
  id: string;
  bookId: string;
  bookTitle: string;
  author: string;
  isbn: string;
  borrowDate: string;
  dueDate: string;
  renewalCount: number;
  maxRenewals: number;
  isOverdue: boolean;
  daysPastDue?: number;
  fine?: number;
}

interface MemberProfileProps {
  member: UnifiedMember;
  onBack: () => void;
  onSendReminder?: (memberId: string, type: 'email' | 'sms') => void;
  onAddFine?: (memberId: string, amount: number, reason: string) => void;
  onSuspendMember?: (memberId: string) => void;
  onReactivateMember?: (memberId: string) => void;
}

interface DueDateStatus {
  background: string;
  border: string;
  icon: React.ReactNode;
  text: string;
}

type StatusFilter = "all" | "returned_on_time" | "returned_late" | "still_borrowed" | "overdue";

interface PunctualityBadge {
  text: string;
  className: string;
}

const MemberBorrowingHistory = ({ memberId, schoolID }: { memberId: string; schoolID: string }) => {
  const [records, setRecords] = useState<BorrowingRecord[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<"borrowDate" | "dueDate" | "returnDate">("borrowDate");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const lookupId = memberId;

  useEffect(() => {
    if (!schoolID || !lookupId) return;

    const recordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
    const q = query(recordsRef, where("lookupId", "==", lookupId), orderBy("borrowDate", "desc"));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const recordData: BorrowingRecord[] = [];
      const bookIds = new Set<string>();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        recordData.push({
          id: doc.id,
          bookId: data.bookId,
          bookTitle: data.bookTitle,
          memberId: data.memberId,
          lookupId: data.lookupId,
          memberName: data.memberName,
          borrowDate: data.borrowDate.toDate().toISOString().split("T")[0],
          dueDate: data.dueDate.toDate().toISOString().split("T")[0],
          returnDate: data.returnDate ? data.returnDate.toDate().toISOString().split("T")[0] : undefined,
          status: data.status,
          fineAmount: data.fineAmount || 0,
          renewCount: data.renewCount || 0,
          maxRenewals: data.maxRenewals || 2,
        });
        bookIds.add(data.bookId);
      });

      // Fetch books for additional info if needed
      const booksRef = collection(db, `schools/${schoolID}/books`);
      const bookPromises = Array.from(bookIds).map(id => getDoc(doc(booksRef, id)).then(snap => snap.data() as Book | undefined));
      const bookDatas = await Promise.all(bookPromises);
      const bookMap = new Map(bookDatas.filter((b): b is Book => !!b).map(b => [b.id, b]));

      setBooks(Array.from(bookMap.values()));
      setRecords(recordData);
    });

    return () => unsubscribe();
  }, [schoolID, lookupId]);

  const getDaysUntilDue = (dueDate: string): number => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getEffectiveStatus = useCallback((record: BorrowingRecord): StatusFilter => {
    const today = new Date();
    if (record.status === "returned") {
      if (record.returnDate) {
        const returnDate = new Date(record.returnDate);
        const due = new Date(record.dueDate);
        return returnDate > due ? "returned_late" : "returned_on_time";
      }
      return "returned_on_time"; // fallback
    } else if (record.status === "borrowed") {
      const due = new Date(record.dueDate);
      if (due < today) {
        return "overdue";
      }
      return "still_borrowed";
    }
    return "still_borrowed"; // fallback
  }, []);

  const getDueDateStatus = (record: BorrowingRecord): DueDateStatus => {
    const today = new Date().toISOString().split("T")[0];
    const effectiveStatus = getEffectiveStatus(record);
    const isOverdue = effectiveStatus === "overdue";
    if (isOverdue) {
      const daysPastDue = Math.floor((new Date(today).getTime() - new Date(record.dueDate).getTime()) / (1000 * 60 * 60 * 24));
      return {
        background: "bg-[#FEF2F2] dark:bg-[#450A0A]",
        border: "border-[#FECACA] dark:border-[#7F1D1D]",
        icon: <AlertTriangle className="h-4 w-4 text-[#EF4444] dark:text-[#F87171]" />,
        text: `${daysPastDue} days overdue`,
      };
    }
    if (record.status === "borrowed") {
      const daysUntilDue = getDaysUntilDue(record.dueDate);
      if (daysUntilDue <= 3) {
        return {
          background: "bg-[#FFF7ED] dark:bg-[#431407]",
          border: "border-[#FED7AA] dark:border-[#7C2D12]",
          icon: <Clock className="h-4 w-4 text-[#F97316] dark:text-[#FDBA74]" />,
          text: daysUntilDue === 0 ? "Due today" : `Due in ${daysUntilDue} days`,
        };
      }
      return {
        background: "bg-[#F0FDF4] dark:bg-[#022C22]",
        border: "border-[#BBF7D0] dark:border-[#166534]",
        icon: <Calendar className="h-4 w-4 text-[#22C55E] dark:text-[#4ADE80]" />,
        text: `Due in ${daysUntilDue} days`,
      };
    }
    return {
      background: "bg-[#F0FDF4] dark:bg-[#022C22]",
      border: "border-[#BBF7D0] dark:border-[#166534]",
      icon: <Calendar className="h-4 w-4 text-[#22C55E] dark:text-[#4ADE80]" />,
      text: "Returned",
    };
  };

  const filteredRecords = useMemo(() => {
    return records
      .filter((record) => {
        const matchesSearch =
          record.bookTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.memberName.toLowerCase().includes(searchTerm.toLowerCase());
        const effectiveStatus = getEffectiveStatus(record);
        const matchesStatus = filterStatus === "all" || effectiveStatus === filterStatus;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (sortBy === "borrowDate") {
          return new Date(b.borrowDate).getTime() - new Date(a.borrowDate).getTime();
        }
        if (sortBy === "dueDate") {
          return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
        }
        if (sortBy === "returnDate") {
          const aDate = a.returnDate ? new Date(a.returnDate).getTime() : 0;
          const bDate = b.returnDate ? new Date(b.returnDate).getTime() : 0;
          return bDate - aDate;
        }
        return 0;
      });
  }, [records, searchTerm, filterStatus, sortBy, getEffectiveStatus]);

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((value: string) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  }, []);

  const getStatusBadge = useCallback((record: BorrowingRecord) => {
    const effectiveStatus = getEffectiveStatus(record);
    switch (effectiveStatus) {
      case "returned_on_time":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#22C55E] dark:bg-[#4ADE80] text-white hover:bg-[#16A34A] dark:hover:bg-[#22C55E] flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            On Time
          </Badge>
        );
      case "returned_late":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#EAB308] dark:bg-[#FACC15] text-white hover:bg-[#CA8A04] dark:hover:bg-[#EAB308] flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Late Return
          </Badge>
        );
      case "still_borrowed":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#6B7280] dark:bg-[#9CA3AF] text-white hover:bg-[#4B5563] dark:hover:bg-[#6B7280] flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Borrowed
          </Badge>
        );
      case "overdue":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#EF4444] dark:bg-[#F87171] text-white hover:bg-[#DC2626] dark:hover:bg-[#EF4444] flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Overdue
          </Badge>
        );
      default:
        return null;
    }
  }, [getEffectiveStatus]);

  const getConditionBadge = (condition?: string) => {
    if (!condition) return <span className="text-gray-600 dark:text-gray-400 text-sm">-</span>;

    switch (condition) {
      case "good":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#22C55E] dark:bg-[#4ADE80] text-white hover:bg-[#16A34A] dark:hover:bg-[#22C55E]">
            Good
          </Badge>
        );
      case "damaged":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#EF4444] dark:bg-[#F87171] text-white hover:bg-[#DC2626] dark:hover:bg-[#EF4444]">
            Damaged
          </Badge>
        );
      case "lost":
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#EF4444] dark:bg-[#F87171] text-white hover:bg-[#DC2626] dark:hover:bg-[#EF4444]">
            Lost
          </Badge>
        );
      default:
        return (
          <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#6B7280] dark:bg-[#9CA3AF] text-white hover:bg-[#4B5563] dark:hover:bg-[#6B7280]">
            {condition}
          </Badge>
        );
    }
  };

  const exportHistory = useCallback(() => {
    const csvContent = [
      ["Book Title", "Author", "ISBN", "Borrow Date", "Due Date", "Return Date", "Status", "Condition", "Fine Paid"].join(","),
      ...filteredRecords.map((record) => {
        const book = books.find(b => b.id === record.bookId);
        const effectiveStatusStr = getEffectiveStatus(record).replace(/_/g, " ");
        return [
          `"${record.bookTitle}"`,
          `"${book?.author || ''}"`,
          `"${book?.isbn || ''}"`,
          record.borrowDate,
          record.dueDate,
          record.returnDate || "",
          effectiveStatusStr,
          '', // condition not in record
          record.fineAmount ? `$${record.fineAmount.toFixed(2)}` : "",
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `borrowing_history_${memberId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredRecords, books, getEffectiveStatus, memberId]);

  const totalFinesPaid = useMemo(() => records.reduce((sum, record) => sum + record.fineAmount, 0), [records]);
  const onTimeReturns = useMemo(() => records.filter((r) => r.status === "returned" && r.returnDate && new Date(r.returnDate) <= new Date(r.dueDate)).length, [records]);
  const totalReturned = useMemo(() => records.filter((r) => r.status === "returned").length, [records]);
  const punctualityRate = useMemo(() => totalReturned > 0 ? Math.round((onTimeReturns / totalReturned) * 100) : 0, [onTimeReturns, totalReturned]);
  const currentlyBorrowed = useMemo(() => records.filter((r) => r.status === "borrowed").length, [records]);

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto { font-family: 'Roboto', sans-serif; }
        `}
      </style>
      <div className="w-full max-w-full flex flex-col p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#DBEAFE] dark:bg-[#1E3A8A] rounded-lg">
                    <BookOpen className="h-5 w-5 text-[#2563EB] dark:text-[#3B82F6]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black dark:text-white">{records.length}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Books Borrowed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#F0FDF4] dark:bg-[#022C22] rounded-lg">
                    <Clock className="h-5 w-5 text-[#22C55E] dark:text-[#4ADE80]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#22C55E] dark:text-[#4ADE80]">{punctualityRate}%</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">On-Time Return Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FEF2F2] dark:bg-[#450A0A] rounded-lg">
                    <DollarSign className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#EF4444] dark:text-[#F87171]">${totalFinesPaid.toFixed(2)}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Fines Paid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FFF7ED] dark:bg-[#431407] rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-[#F97316] dark:text-[#FDBA74]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#F97316] dark:text-[#FDBA74]">{currentlyBorrowed}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Currently Borrowed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
            <CardHeader>
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Borrowing History</CardTitle>
                <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                  <div className="relative flex-1 sm:w-[256px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search by title, author, or ISBN..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 rounded-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      autoComplete="off"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={(value: StatusFilter) => setFilterStatus(value)}>
                    <SelectTrigger className="w-full sm:w-[180px] h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="returned_on_time">On Time</SelectItem>
                      <SelectItem value="returned_late">Late Return</SelectItem>
                      <SelectItem value="still_borrowed">Still Borrowed</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={(value: "borrowDate" | "dueDate" | "returnDate") => setSortBy(value)}>
                    <SelectTrigger className="w-full sm:w-[180px] h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                      <SelectValue placeholder="Sort By" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <SelectItem value="borrowDate">Borrow Date</SelectItem>
                      <SelectItem value="dueDate">Due Date</SelectItem>
                      <SelectItem value="returnDate">Return Date</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportHistory}
                    className="m-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] flex items-center gap-2"
                  >
                    <Download className="h-4 w-4 text-gray-400" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                      <TableHead className="min-w-[120px] max-w-[150px] text-black dark:text-white">Book Details</TableHead>
                      <TableHead className="hidden md:table-cell w-[100px] text-black dark:text-white">ISBN</TableHead>
                      <TableHead className="w-[80px] text-black dark:text-white">Borrow Date</TableHead>
                      <TableHead className="w-[120px] text-black dark:text-white">Due Date</TableHead>
                      <TableHead className="hidden sm:table-cell w-[80px] text-black dark:text-white">Return Date</TableHead>
                      <TableHead className="hidden sm:table-cell w-[80px] text-black dark:text-white">Status</TableHead>
                      <TableHead className="hidden lg:table-cell w-[60px] text-black dark:text-white">Condition</TableHead>
                      <TableHead className="hidden lg:table-cell w-[60px] text-black dark:text-white">Fine</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRecords.map((record) => {
                      const dueDateStatus = getDueDateStatus(record);
                      const effectiveStatus = getEffectiveStatus(record);
                      // no-op flag removed (was unused): determine badges directly when needed
                      return (
                        <TableRow
                          key={record.id}
                          className={`border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929] ${
                            effectiveStatus === "overdue" ? "border-l-4 border-l-[#EF4444]" : effectiveStatus === "still_borrowed" && getDaysUntilDue(record.dueDate) <= 3 ? "border-l-4 border-l-[#F97316]" : ""
                          }`}
                        >
                          <TableCell className="py-2">
                            <div>
                              <p className="text-sm font-medium text-black dark:text-white truncate">{record.bookTitle}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{record.memberName}</p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell font-mono text-sm text-black dark:text-white py-2 truncate">
                            {books.find(b => b.id === record.bookId)?.isbn || ''}
                          </TableCell>
                          <TableCell className="text-sm text-black dark:text-white py-2">
                            {new Date(record.borrowDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="py-2">
                            <div className={`px-2 py-1 rounded-md ${dueDateStatus.background} ${dueDateStatus.border} border`}>
                              <div className="flex items-center gap-2">
                                {dueDateStatus.icon}
                                <div>
                                  <p className="text-sm font-medium text-black dark:text-white">
                                    {new Date(record.dueDate).toLocaleDateString()}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">{dueDateStatus.text}</p>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-sm text-black dark:text-white py-2">
                            {record.returnDate ? new Date(record.returnDate).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell py-2">{getStatusBadge(record)}</TableCell>
                          <TableCell className="hidden lg:table-cell py-2">{getConditionBadge(undefined)}</TableCell>
                          <TableCell className="hidden lg:table-cell py-2">
                            {record.fineAmount > 0 ? (
                              <span className="text-[#EF4444] dark:text-[#F87171] font-medium text-sm">${record.fineAmount.toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {filteredRecords.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={handleRowsPerPageChange}
                >
                  <SelectTrigger className="w-[80px] h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 5) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                    return false;
                  })
                  .map((page) => (
                    <Button
                      key={page}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={`m-2 h-8 w-8 p-0 ${
                        currentPage === page
                          ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white border-[#2563EB] dark:border-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
                          : "bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Button>
              </div>
            </div>
          )}

          {filteredRecords.length === 0 && (
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="text-center py-12">
                <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400">No records found</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Try adjusting your search or filters to see more results.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

const MemberCurrentBooks = ({ memberId, schoolID }: { memberId: string; schoolID: string }) => {
  const [currentBooks, setCurrentBooks] = useState<CurrentBook[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { toast } = useToast();

  const lookupId = memberId;

  useEffect(() => {
    if (!schoolID || !lookupId) return;

    const recordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
    const q = query(recordsRef, where("lookupId", "==", lookupId), where("status", "==", "borrowed"), orderBy("borrowDate", "desc"));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const recordData: CurrentBook[] = [];
      const bookIds = new Set<string>();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const borrowDate = data.borrowDate.toDate().toISOString().split("T")[0];
        const dueDate = data.dueDate.toDate().toISOString().split("T")[0];
        const today = new Date().toISOString().split("T")[0];
        const isOverdue = new Date(dueDate) < new Date(today);
        const daysPastDue = isOverdue ? Math.floor((new Date(today).getTime() - new Date(dueDate).getTime()) / (1000 * 60 * 60 * 24)) : undefined;
        const fine = isOverdue ? (daysPastDue! * 1.00) : undefined;
        recordData.push({
          id: doc.id,
          bookId: data.bookId,
          bookTitle: data.bookTitle,
          author: '', // to be filled
          isbn: '', // to be filled
          borrowDate,
          dueDate,
          renewalCount: data.renewCount || 0,
          maxRenewals: data.maxRenewals || 2,
          isOverdue,
          daysPastDue,
          fine,
        });
        bookIds.add(data.bookId);
      });

      // Fetch books
      const booksRef = collection(db, `schools/${schoolID}/books`);
      const bookPromises = Array.from(bookIds).map(id => getDoc(doc(booksRef, id)).then(snap => snap.data() as Book | undefined));
      const bookDatas = await Promise.all(bookPromises);
      const bookMap = new Map(bookDatas.filter((b): b is Book => !!b).map(b => [b.id, b]));

      const fullData = recordData.map(r => ({
        ...r,
        author: bookMap.get(r.bookId)?.author || '',
        isbn: bookMap.get(r.bookId)?.isbn || '',
      }));

      setCurrentBooks(fullData);
    });

    return () => unsubscribe();
  }, [schoolID, lookupId]);

  const handleRenewBook = async (bookId: string) => {
    const record = currentBooks.find(b => b.id === bookId);
    if (!record || record.renewalCount >= record.maxRenewals || record.isOverdue) {
      toast({
        title: "Error",
        description: record?.isOverdue ? "Cannot renew overdue books" : "Maximum renewals reached",
        variant: "destructive",
      });
      return;
    }

    if (!schoolID) return;

    const newDueDate = addDays(new Date(record.dueDate), 30);
    try {
      const borrowRef = doc(db, `schools/${schoolID}/borrowRecords`, bookId);
      await updateDoc(borrowRef, {
        dueDate: newDueDate,
        renewCount: increment(1),
      });
      toast({
        title: "Book Renewed",
        description: "Due date extended by 30 days",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to renew: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const getDaysUntilDue = (dueDate: string): number => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDueDateStatus = (book: CurrentBook): DueDateStatus => {
    if (book.isOverdue) {
      return {
        background: "bg-[#FEF2F2] dark:bg-[#450A0A]",
        border: "border-[#FECACA] dark:border-[#7F1D1D]",
        icon: <AlertTriangle className="h-4 w-4 text-[#EF4444] dark:text-[#F87171]" />,
        text: `${book.daysPastDue} days overdue`,
      };
    }
    const daysUntilDue = getDaysUntilDue(book.dueDate);
    if (daysUntilDue <= 3) {
      return {
        background: "bg-[#FFF7ED] dark:bg-[#431407]",
        border: "border-[#FED7AA] dark:border-[#7C2D12]",
        icon: <Clock className="h-4 w-4 text-[#F97316] dark:text-[#FDBA74]" />,
        text: daysUntilDue === 0 ? "Due today" : `Due in ${daysUntilDue} days`,
      };
    }
    return {
      background: "bg-[#F0FDF4] dark:bg-[#022C22]",
      border: "border-[#BBF7D0] dark:border-[#166534]",
      icon: <Calendar className="h-4 w-4 text-[#22C55E] dark:text-[#4ADE80]" />,
      text: `Due in ${daysUntilDue} days`,
    };
  };

  const getStatusBadge = (book: CurrentBook) => {
    if (book.isOverdue) {
      return (
        <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white">
          Overdue
        </Badge>
      );
    }
    const daysUntilDue = getDaysUntilDue(book.dueDate);
    if (daysUntilDue <= 3) {
      return (
        <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#F97316] dark:bg-[#FDBA74] hover:bg-[#EA580C] dark:hover:bg-[#F97316] text-white">
          Due Soon
        </Badge>
      );
    }
    return (
      <Badge className="text-xs px-2 py-0.5 font-medium w-fit max-w-[100px] truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white">
        Active
      </Badge>
    );
  };

  const totalFines = useMemo(() => currentBooks.reduce((sum, book) => sum + (book.fine || 0), 0), [currentBooks]);
  const overdueCount = useMemo(() => currentBooks.filter((book) => book.isOverdue).length, [currentBooks]);
  const dueSoonCount = useMemo(() => currentBooks.filter((book) => !book.isOverdue && getDaysUntilDue(book.dueDate) <= 3).length, [currentBooks]);

  const totalPages = Math.ceil(currentBooks.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedBooks = currentBooks.slice(startIndex, endIndex);

  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((value: string) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  }, []);

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto { font-family: 'Roboto', sans-serif; }
        `}
      </style>
      <div className="w-full max-w-full flex flex-col p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#DBEAFE] dark:bg-[#1E3A8A] rounded-lg">
                    <BookOpen className="h-5 w-5 text-[#2563EB] dark:text-[#3B82F6]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black dark:text-white">{currentBooks.length}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Currently Borrowed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FEF2F2] dark:bg-[#450A0A] rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#EF4444] dark:text-[#F87171]">{overdueCount}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Overdue Books</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FFF7ED] dark:bg-[#431407] rounded-lg">
                    <Clock className="h-5 w-5 text-[#F97316] dark:text-[#FDBA74]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#F97316] dark:text-[#FDBA74]">{dueSoonCount}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Due Soon (≤3 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FEF2F2] dark:bg-[#450A0A] rounded-lg">
                    <DollarSign className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#EF4444] dark:text-[#F87171]">${totalFines.toFixed(2)}</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Outstanding Fines</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Current Books List */}
          <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] transition-colors">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-black dark:text-white">Current Borrowed Books</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {currentBooks.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400">No books currently borrowed</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    All books have been returned or no books borrowed yet.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                        <TableHead className="min-w-[120px] max-w-[150px] text-black dark:text-white">Book Details</TableHead>
                        <TableHead className="hidden md:table-cell w-[100px] text-black dark:text-white">ISBN</TableHead>
                        <TableHead className="w-[80px] text-black dark:text-white">Borrow Date</TableHead>
                        <TableHead className="w-[120px] text-black dark:text-white">Due Date</TableHead>
                        <TableHead className="hidden sm:table-cell w-[80px] text-black dark:text-white">Status</TableHead>
                        <TableHead className="hidden lg:table-cell w-[60px] text-black dark:text-white">Renewals</TableHead>
                        <TableHead className="hidden lg:table-cell w-[60px] text-black dark:text-white">Fine</TableHead>
                        <TableHead className="w-[80px] text-right text-black dark:text-white">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedBooks.map((book) => {
                        const dueDateStatus = getDueDateStatus(book);
                        return (
                          <TableRow
                            key={book.id}
                            className={`border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929] ${
                              book.isOverdue
                                ? "border-l-4 border-l-[#EF4444]"
                                : getDaysUntilDue(book.dueDate) <= 3
                                ? "border-l-4 border-l-[#F97316]"
                                : ""
                            }`}
                          >
                            <TableCell className="py-2">
                              <div>
                                <p className="text-sm font-medium text-black dark:text-white truncate">{book.bookTitle}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{book.author}</p>
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-sm text-black dark:text-white py-2 truncate">
                              {book.isbn}
                            </TableCell>
                            <TableCell className="text-sm text-black dark:text-white py-2">
                              {new Date(book.borrowDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="py-2">
                              <div className={`px-2 py-1 rounded-md ${dueDateStatus.background} ${dueDateStatus.border} border`}>
                                <div className="flex items-center gap-2">
                                  {dueDateStatus.icon}
                                  <div>
                                    <p className="text-sm font-medium text-black dark:text-white">
                                      {new Date(book.dueDate).toLocaleDateString()}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{dueDateStatus.text}</p>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell py-2">{getStatusBadge(book)}</TableCell>
                            <TableCell className="hidden lg:table-cell py-2">
                              <div className="text-sm">
                                <span
                                  className={
                                    book.renewalCount >= book.maxRenewals
                                      ? "text-[#EF4444] dark:text-[#F87171]"
                                      : "text-gray-600 dark:text-gray-400"
                                  }
                                >
                                  {book.renewalCount}/{book.maxRenewals}
                                </span>
                                <p className="text-sm text-gray-600 dark:text-gray-400">renewals</p>
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell py-2">
                              {book.fine ? (
                                <span className="text-[#EF4444] dark:text-[#F87171] font-medium text-sm">
                                  ${book.fine.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-600 dark:text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right py-2">
                              <div className="flex gap-0.5 justify-end items-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRenewBook(book.id)}
                                  disabled={book.renewalCount >= book.maxRenewals || book.isOverdue}
                                  title={
                                    book.isOverdue
                                      ? "Cannot renew overdue books"
                                      : book.renewalCount >= book.maxRenewals
                                      ? "Maximum renewals reached"
                                      : "Renew book"
                                  }
                                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <RefreshCw className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  title="View book details"
                                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                                >
                                  <ArrowRight className="h-4 w-4 text-gray-400" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {currentBooks.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={handleRowsPerPageChange}
                >
                  <SelectTrigger className="w-[80px] h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 5) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                    return false;
                  })
                  .map((page) => (
                    <Button
                      key={page}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={`m-2 h-8 w-8 p-0 ${
                        currentPage === page
                          ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white border-[#2563EB] dark:border-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
                          : "bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="m-2 h-8 w-8 p-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </Button>
              </div>
            </div>
          )}

          {/* Overdue Alert */}
          {overdueCount > 0 && (
            <Card className="bg-[#FEF2F2] dark:bg-[#450A0A] border-[#FECACA] dark:border-[#7F1D1D] transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  <div className="flex-1">
                    <h3 className="font-medium text-[#7F1D1D] dark:text-[#FECACA]">
                      {overdueCount} {overdueCount === 1 ? "book is" : "books are"} overdue
                    </h3>
                    <p className="text-sm text-[#7F1D1D] dark:text-[#FECACA]">
                      Please return or renew overdue books to avoid additional fines.
                      {totalFines > 0 && ` Current fine total: $${totalFines.toFixed(2)} `}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

const MemberActivitySummary = ({ memberId, schoolID }: { memberId: string; schoolID: string }) => {
  const [records, setRecords] = useState<BorrowingRecord[]>([]);
  const [, setBooks] = useState<Book[]>([]);

  const lookupId = memberId;

  useEffect(() => {
    if (!schoolID || !lookupId) return;

    const recordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
    const q = query(recordsRef, where("lookupId", "==", lookupId), orderBy("borrowDate", "desc"));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const recordData: BorrowingRecord[] = [];
      const bookIds = new Set<string>();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        recordData.push({
          id: doc.id,
          bookId: data.bookId,
          bookTitle: data.bookTitle,
          memberId: data.memberId,
          lookupId: data.lookupId,
          memberName: data.memberName,
          borrowDate: data.borrowDate.toDate().toISOString().split("T")[0],
          dueDate: data.dueDate.toDate().toISOString().split("T")[0],
          returnDate: data.returnDate ? data.returnDate.toDate().toISOString().split("T")[0] : undefined,
          status: data.status,
          fineAmount: data.fineAmount || 0,
          renewCount: data.renewCount || 0,
          maxRenewals: data.maxRenewals || 2,
        });
        bookIds.add(data.bookId);
      });

      const booksRef = collection(db, `schools/${schoolID}/books`);
      const bookPromises = Array.from(bookIds).map(id => getDoc(doc(booksRef, id)).then(snap => snap.data() as Book | undefined));
      const bookDatas = await Promise.all(bookPromises);
      const bookMap = new Map(bookDatas.filter((b): b is Book => !!b).map(b => [b.id, b]));

      const fullData = recordData.map(r => ({
        ...r,
        category: bookMap.get(r.bookId)?.category || 'Other',
      })) as BorrowingRecord[];

      setRecords(fullData);
      setBooks(Array.from(bookMap.values()));
    });

    return () => unsubscribe();
  }, [schoolID, lookupId]);

  const data = useMemo(() => {
    const totalBooksBorrowed = records.length;
    const totalFinesPaid = records.reduce((sum, r) => sum + r.fineAmount, 0);
    const returnedRecords = records.filter(r => r.status === "returned");
    const onTimeReturns = returnedRecords.filter(r => r.returnDate && new Date(r.returnDate) <= new Date(r.dueDate)).length;
    const avgReturnPunctuality = returnedRecords.length > 0 ? Math.round((onTimeReturns / returnedRecords.length) * 100) : 0;

    const sortedReturned = [...returnedRecords].sort((a, b) => new Date(b.returnDate!).getTime() - new Date(a.returnDate!).getTime());
    let streak = 0;
    for (const r of sortedReturned) {
      if (r.returnDate && new Date(r.returnDate) <= new Date(r.dueDate)) {
        streak++;
      } else {
        break;
      }
    }
    const currentStreak = streak;

    const genreCount = records.reduce((acc, r) => {
      acc[r.category || 'Other'] = (acc[r.category || 'Other'] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const total = records.length;
    const favoriteGenres = Object.entries(genreCount)
      .map(([name, count]) => ({ name, count, percentage: total > 0 ? Math.round((count / total) * 100) : 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const monthly = records.reduce((acc, r) => {
      const month = new Date(r.borrowDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      acc[month] = (acc[month] || { borrowed: 0, returned: 0 });
      acc[month].borrowed += 1;
      if (r.status === "returned" && r.returnDate) {
        const returnMonth = new Date(r.returnDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        acc[returnMonth] = (acc[returnMonth] || { borrowed: 0, returned: 0 });
        acc[returnMonth].returned += 1;
      }
      return acc;
    }, {} as Record<string, {borrowed: number, returned: number}>);
    const monthlyActivity = Object.entries(monthly).map(([month, {borrowed, returned}]) => ({ month, borrowed, returned })).slice(-6);

    const achievements = [
      { name: "Bookworm", description: "Borrowed +20 books this year", achieved: totalBooksBorrowed >= 20 },
      { name: "Punctual Reader", description: "90% on-time return rate", achieved: avgReturnPunctuality >= 90 },
      { name: "Diverse Reader", description: "Borrowed from 5+ categories", achieved: Object.keys(genreCount).length >= 5 },
      { name: "Monthly Regular", description: "Borrowed every month", achieved: monthlyActivity.length >= 6 },
    ];

    const recentActivity = records.slice(0, 4).reverse().map(r => ({
      action: r.status === "returned" ? "Returned" : "Borrowed",
      book: r.bookTitle,
      date: r.borrowDate,
      onTime: r.status === "returned" && r.returnDate && new Date(r.returnDate) <= new Date(r.dueDate),
    }));

    return {
      totalBooksBorrowed,
      totalFinesPaid,
      avgReturnPunctuality,
      currentStreak,
      favoriteGenres,
      monthlyActivity,
      achievements,
      recentActivity,
    };
  }, [records]);

  const getPunctualityBadge = useCallback((percentage: number): PunctualityBadge => {
    if (percentage >= 90) {
      return {
        text: "Excellent",
        className:
          "text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white",
      };
    }
    if (percentage >= 75) {
      return {
        text: "Good",
        className:
          "text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#F97316] dark:bg-[#FDBA74] hover:bg-[#EA580C] dark:hover:bg-[#F97316] text-white",
      };
    }
    return {
      text: "Needs Improvement",
      className:
        "text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white",
    };
  }, []);

  const getActivityBadge = useCallback((action: string) => {
    const isReturned = action === "Returned";
    return (
      <Badge
        className={
          isReturned
            ? "text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white"
            : "text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white"
        }
      >
        {action}
      </Badge>
    );
  }, []);

  const punctualityBadge = getPunctualityBadge(data.avgReturnPunctuality);

  const COLORS = ["#2563EB", "#22C55E", "#F97316", "#EF4444", "#6B7280"];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="space-y-6">
          {/* Key Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#DBEAFE] dark:bg-[#1E3A8A] rounded-lg">
                    <BookOpen className="h-5 w-5 text-[#2563EB] dark:text-[#3B82F6]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black dark:text-white">{data.totalBooksBorrowed}</div>
                    <p className="text-sm text-muted-foreground">Books This Year</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FEF2F2] dark:bg-[#450A0A] rounded-lg">
                    <DollarSign className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black dark:text-white">${data.totalFinesPaid.toFixed(2)}</div>
                    <p className="text-sm text-muted-foreground">Total Fines Paid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#F0FDF4] dark:bg-[#022C22] rounded-lg">
                    <Clock className="h-5 w-5 text-[#22C55E] dark:text-[#4ADE80]" />
                  </div>
                  <div>
                    <div
                      className={
                        data.avgReturnPunctuality >= 90
                          ? "text-2xl font-bold text-[#22C55E] dark:text-[#4ADE80]"
                          : data.avgReturnPunctuality >= 75
                          ? "text-2xl font-bold text-[#F97316] dark:text-[#FDBA74]"
                          : "text-2xl font-bold text-[#EF4444] dark:text-[#F87171]"
                      }
                    >
                      {data.avgReturnPunctuality}%
                    </div>
                    <p className="text-sm text-muted-foreground">On-Time Returns</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#FFF7ED] dark:bg-[#431407] rounded-lg">
                    <TrendingUp className="h-5 w-5 text-[#F97316] dark:text-[#FDBA74]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black dark:text-white">{data.currentStreak}</div>
                    <p className="text-sm text-muted-foreground">Day Streak</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Monthly Reading Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.monthlyActivity}>
                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" className="dark:stroke-[#1E293B]" />
                    <XAxis dataKey="month" stroke="#64748B" className="dark:stroke-[#94A3B8]" />
                    <YAxis stroke="#64748B" className="dark:stroke-[#94A3B8]" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #F1F5F9",
                        borderRadius: "4px",
                      }}
                      wrapperStyle={{ zIndex: 10 }}
                      cursor={{ fill: "#F8FAFC" }}
                    />
                    <Bar dataKey="borrowed" fill="#2563EB" name="Borrowed" />
                    <Bar dataKey="returned" fill="#22C55E" name="Returned" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Reading Preferences</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.favoriteGenres}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => {
                        const genre = data.favoriteGenres[entry.index];
                        return `${entry.name} ${genre ? genre.percentage : ""}%`;
                      }}
                      outerRadius={80}
                      dataKey="count"
                    >
                      {data.favoriteGenres.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #F1F5F9",
                        borderRadius: "4px",
                      }}
                      wrapperStyle={{ zIndex: 10 }}
                      cursor={{ fill: "#F8FAFC" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Reading Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Return Punctuality</span>
                    <Badge className={punctualityBadge.className}>{punctualityBadge.text}</Badge>
                  </div>
                  <Progress
                    value={data.avgReturnPunctuality}
                    className={
                      data.avgReturnPunctuality >= 90
                        ? "h-2 bg-[#22C55E] dark:bg-[#4ADE80]"
                        : data.avgReturnPunctuality >= 75
                        ? "h-2 bg-[#F97316] dark:bg-[#FDBA74]"
                        : "h-2 bg-[#EF4444] dark:bg-[#F87171]"
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {data.avgReturnPunctuality}% of books returned on time
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Reading Goal Progress</span>
                    <span className="text-sm text-muted-foreground">{data.totalBooksBorrowed}/50</span>
                  </div>
                  <Progress value={(data.totalBooksBorrowed / 50) * 100} className="h-2 bg-[#2563EB] dark:bg-[#3B82F6]" />
                  <p className="text-xs text-muted-foreground mt-1">
                    {50 - data.totalBooksBorrowed} more books to reach yearly goal
                  </p>
                </div>
                <div className="pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                  <h4 className="font-medium mb-3 text-black dark:text-white">Most Borrowed Genres</h4>
                  <div className="space-y-2">
                    {data.favoriteGenres.slice(0, 3).map((genre, index) => (
                      <div key={genre.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-black dark:text-white">{genre.name}</span>
                        </div>
                        <span className="text-muted-foreground">{genre.count} books</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Achievements & Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-black dark:text-white">
                    <Award className="h-4 w-4 text-[#F97316] dark:text-[#FDBA74]" />
                    Achievements
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {data.achievements.map((achievement) => (
                      <div
                        key={achievement.name}
                        className={
                          achievement.achieved
                            ? "p-3 rounded-lg border bg-[#F0FDF4] dark:bg-[#022C22] border-[#BBF7D0] dark:border-[#166534]"
                            : "p-3 rounded-lg border bg-[#F1F5F9] dark:bg-[#1E293B] border-[#E2E8F0] dark:border-[#334155]"
                        }
                      >
                        <div
                          className={
                            achievement.achieved
                              ? "text-sm font-medium text-[#22C55E] dark:text-[#4ADE80]"
                              : "text-sm font-medium text-gray-600 dark:text-gray-400"
                          }
                        >
                          {achievement.name}
                        </div>
                        <div
                          className={
                            achievement.achieved
                              ? "text-xs mt-1 text-[#16A34A] dark:text-[#22C55E]"
                              : "text-xs mt-1 text-gray-500 dark:text-gray-500"
                          }
                        >
                          {achievement.description}
                        </div>
                        {achievement.achieved && (
                          <Award className="h-4 w-4 text-[#22C55E] dark:text-[#4ADE80] mx-auto mt-2" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-3 text-black dark:text-white">Recent Activity</h4>
                  <div className="space-y-2">
                    {data.recentActivity.map((activity, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-sm p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#101929]"
                      >
                        <div className="flex items-center gap-2">
                          {getActivityBadge(activity.action)}
                          <span className="font-medium text-black dark:text-white">{activity.book}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{activity.date}</span>
                          {activity.action === "Returned" && (
                            <div
                              className={
                                activity.onTime
                                  ? "w-2 h-2 rounded-full bg-[#22C55E] dark:bg-[#4ADE80]"
                                  : "w-2 h-2 rounded-full bg-[#EF4444] dark:bg-[#F87171]"
                              }
                              title={activity.onTime ? "On time" : "Late"}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export const MemberProfile = ({ 
  member, 
  onBack, 
  onSendReminder: propOnSendReminder,
  onAddFine: propOnAddFine,
  onSuspendMember: propOnSuspendMember,
  onReactivateMember: propOnReactivateMember 
}: MemberProfileProps) => {
  const [activeTab, setActiveTab] = useState<"overview" | "current" | "history">("overview");
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [isFineDialogOpen, setIsFineDialogOpen] = useState(false);
  const [fineAmount, setFineAmount] = useState("");
  const [fineReason, setFineReason] = useState("");
  const { toast } = useToast();
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          const retryAffiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          selectedAffiliation = retryAffiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
            throw new Error("Invalid token claims");
          }
        }

        const selectedSchoolID = selectedAffiliation.schoolID || null;
        setSchoolID(selectedSchoolID);
        setLoading(false);
      } catch (error: any) {
        setError("Failed to verify permissions");
        setLoading(false);
        toast({
          title: "Missing Permissions",
          description: "You lack the necessary permissions.",
          variant: "destructive",
        });
        navigate("/login-page");
      }
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  const getStatusBadge = useCallback((status: "active" | "suspended" | "expired") => {
    let badgeClass = "";
    let label = status.charAt(0).toUpperCase() + status.slice(1);
    let icon = null;

    switch (status) {
      case "active":
        badgeClass = "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate gap-1";
        break;
      case "suspended":
        badgeClass = "bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate gap-1";
        icon = <Ban className="h-4 w-4" />;
        break;
      case "expired":
        badgeClass = "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate gap-1";
        icon = <AlertTriangle className="h-4 w-4" />;
        break;
      default:
        badgeClass = "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate gap-1";
    }

    return (
      <Badge className={badgeClass}>
        {icon}
        {label}
      </Badge>
    );
  }, []);

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !schoolID) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const lookupId = member.memberId || member.id;

  const handleSendReminder = async (type: 'email' | 'sms') => {
    try {
      const remindersRef = collection(db, `schools/${schoolID}/reminders`);
      await addDoc(remindersRef, {
        lookupId,
        memberName: member.name,
        type,
        date: serverTimestamp(),
        sent: true,
      });
      propOnSendReminder?.(member.id, type);
      setIsReminderDialogOpen(false);
      toast({
        title: "Reminder Sent",
        description: `${type === 'email' ? 'Email' : 'SMS'} reminder sent to ${member.name}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to send reminder: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleAddFine = async () => {
    if (!fineAmount || !fineReason) {
      toast({
        title: "Error",
        description: "Please enter both amount and reason",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const finesRef = collection(db, `schools/${schoolID}/fines`);
      await addDoc(finesRef, {
        lookupId,
        memberName: member.name,
        amount: parseFloat(fineAmount),
        reason: fineReason,
        date: serverTimestamp(),
        paid: false,
      });
      propOnAddFine?.(member.id, parseFloat(fineAmount), fineReason);
      setIsFineDialogOpen(false);
      setFineAmount("");
      setFineReason("");
      toast({
        title: "Fine Added",
        description: `$${fineAmount} fine added to ${member.name}'s account`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to add fine: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleSuspendReactivate = async () => {
    const newStatus = member.status === "suspended" ? "active" : "suspended";
    const basePath = member.role === "student" ? "students" : "staff";
    const memberRef = doc(db, `schools/${schoolID}/${basePath}`, member.id);
    try {
      await updateDoc(memberRef, { status: newStatus });
      if (newStatus === "active") {
        propOnReactivateMember?.(member.id);
      } else {
        propOnSuspendMember?.(member.id);
      }
      toast({
        title: newStatus === "suspended" ? "Member Suspended" : "Member Reactivated",
        description: `${member.name} has been ${newStatus === "suspended" ? "suspended" : "reactivated"}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update status: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
        `}
      </style>
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onBack}
              className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Members
            </Button>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-black dark:text-white">{member.name}</h2>
              <p className="text-sm text-muted-foreground">Member ID: {member.memberId}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsReminderDialogOpen(true)}
                className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
              >
                <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                Send Reminder
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsFineDialogOpen(true)}
                className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
              >
                Add Fine
              </Button>
              <Button 
                variant={member.status === "suspended" ? "default" : "destructive"}
                size="sm"
                onClick={handleSuspendReactivate}
                className={`m-2 h-10 ${
                  member.status === "suspended" 
                    ? "bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]" 
                    : "bg-[#EF4444] hover:bg-[#DC2626] dark:hover:bg-[#F87171]"
                }`}
              >
                {member.status === "suspended" ? (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Reactivate
                  </>
                ) : (
                  <>
                    <Ban className="h-4 w-4 mr-2" />
                    Suspend
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Member Info Card */}
          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-black dark:text-white">Member Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-black dark:text-white">{member.email}</p>
                    </div>
                  </div>
                  {member.phone && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Phone</p>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm text-black dark:text-white">{member.phone}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Role</p>
                    <Badge 
                      className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${
                        member.role === "staff" 
                          ? "bg-[#3B82F6] dark:bg-[#60A5FA] hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white" 
                          : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white"
                      }`}
                    >
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </Badge>
                  </div>
                  {member.role === "student" && member.class && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Class/Grade</p>
                      <p className="text-sm text-black dark:text-white">{member.class} {member.grade && `- ${member.grade}`}</p>
                    </div>
                  )}
                  {member.role === "staff" && member.department && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Department</p>
                      <p className="text-sm text-black dark:text-white">{member.department}</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Membership Status</p>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(member.status)}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Join Date</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-black dark:text-white">{new Date(member.joinDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Books Borrowed</p>
                    <p className="text-sm font-medium text-black dark:text-white">
                      {member.borrowedBooks} / {member.maxBorrowLimit}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for different sections */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "overview" | "current" | "history") }>
            <TabsList className="grid w-full grid-cols-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <TabsTrigger 
                value="overview" 
                className="text-sm font-medium text-black dark:text-white data-[state=active]:bg-[#2563EB] data-[state=active]:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
              >
                Activity Summary
              </TabsTrigger>
              <TabsTrigger 
                value="current" 
                className="text-sm font-medium text-black dark:text-white data-[state=active]:bg-[#2563EB] data-[state=active]:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
              >
                Current Books
              </TabsTrigger>
              <TabsTrigger 
                value="history" 
                className="text-sm font-medium text-black dark:text-white data-[state=active]:bg-[#2563EB] data-[state=active]:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
              >
                Borrowing History
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="mt-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardContent className="p-6">
                  <MemberActivitySummary memberId={lookupId} schoolID={schoolID} />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="current" className="mt-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardContent className="p-6">
                  <MemberCurrentBooks memberId={lookupId} schoolID={schoolID} />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="history" className="mt-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardContent className="p-6">
                  <MemberBorrowingHistory memberId={lookupId} schoolID={schoolID} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Send Reminder Dialog */}
          <Dialog open={isReminderDialogOpen} onOpenChange={setIsReminderDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-md bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-black dark:text-white">Send Reminder to {member.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Choose how to send the reminder about overdue books or library notifications.
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleSendReminder('email')} 
                    className="flex-1 m-2 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] h-10"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button 
                    onClick={() => handleSendReminder('sms')} 
                    variant="outline" 
                    className="flex-1 m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  >
                    <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                    Send SMS
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Fine Dialog */}
          <Dialog open={isFineDialogOpen} onOpenChange={setIsFineDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-md bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-black dark:text-white">Add Fine for {member.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fine Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={fineAmount}
                    onChange={(e) => setFineAmount(e.target.value)}
                    className="mt-1 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Reason</label>
                  <Textarea
                    value={fineReason}
                    onChange={(e) => setFineReason(e.target.value)}
                    className="mt-1 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                    placeholder="e.g., Late return, Damaged book, Lost book"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAddFine} 
                    className="flex-1 m-2 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] h-10"
                  >
                    Add Fine
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsFineDialogOpen(false)}
                    className="flex-1 m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};